// StudentProgressReport.jsx
import React, { useEffect, useState, useRef } from 'react';
import API from './api';
import * as XLSX from 'xlsx';

const domainLabels = {
  cognitive: 'Cognitive',
  language: 'Language',
  gross_motor: 'Gross Motor',
  fine_motor: 'Fine Motor',
  social: 'Social',
  self_help: 'Self Help',
  other: 'Other'
};

// 6 glavnih oblasti (služi za uniju domena u GOV)
const sixDomains = ['cognitive','language','gross_motor','fine_motor','social','self_help'];

const periodOptions = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_month_gov', label: 'This month (gov)' },
  { value: 'last_month_gov', label: 'Last month (gov)' },
  { value: 'first_half', label: 'First 6 months' },
  { value: 'second_half', label: 'Second 6 months' },
  { value: 'custom', label: 'Custom period' }
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  if (typeof dateStr === 'object' && dateStr.toISOString) {
    return dateStr.toISOString().slice(0, 10);
  }
  return String(dateStr).slice(0, 10);
};

// --- helpers za starost u mesecima ---
const parseYMDDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) {
    return Number.isNaN(val.getTime()) ? null : val;
  }
  const s = String(val).slice(0, 10); // "YYYY-MM-DD"
  const parts = s.split('-').map(Number);
  if (parts.length === 3 && parts.every(x => Number.isFinite(x))) {
    const [y, m, d] = parts;
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(val);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const calcAgeMonths = (dobVal, refVal) => {
  const dob = parseYMDDate(dobVal);
  const ref = parseYMDDate(refVal);
  if (!dob || !ref) return null;

  let months =
    (ref.getFullYear() - dob.getFullYear()) * 12 +
    (ref.getMonth() - dob.getMonth());

  if (ref.getDate() < dob.getDate()) {
    months -= 1;
  }
  if (months < 0) months = 0;
  return months;
};

function renderCommentCell(comments) {
  if (!comments) return null;
  const lines = comments.split('\n');
  const last = lines[lines.length - 1].trim();
  if (last.startsWith('(') && last.endsWith(')')) {
    const hasMain = lines.length > 1 && lines.slice(0, -1).join('\n').trim() !== '';
    if (hasMain) {
      return (
        <>
          {lines.slice(0, -1).map((line, idx) => (
            <React.Fragment key={idx}>
              {line}
              <br />
            </React.Fragment>
          ))}
          <span className="text-xs text-gray-500 italic print-comment-teacher">{last}</span>
        </>
      );
    } else {
      return <span className="text-xs text-gray-500 italic print-comment-teacher">{last}</span>;
    }
  }
  return comments.split('\n').map((line, idx, arr) => (
    <React.Fragment key={idx}>
      {line}
      {idx < arr.length - 1 && <br />}
    </React.Fragment>
  ));
}

function StudentProgressReport() {
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState({
    student_id: '',
    period: 'this_month',
    date_from: '',
    date_to: ''
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [parentInfo, setParentInfo] = useState('');
  const reportRef = useRef();

  const isGov = (p) => p === 'this_month_gov' || p === 'last_month_gov';

  // brand info
  const [brandName, setBrandName] = useState('EarlyCare');
  const [brandLogoUrl, setBrandLogoUrl] = useState('/VisitOMS_logo.png');

  // Učitaj studente
  useEffect(() => {
    (async () => {
      const res = await API.get('/students');
      setStudents(res.data || []);
    })();
  }, []);

  // Učitaj brand (name + logo)
  useEffect(() => {
    (async () => {
      try {
        const [bRes, logoRes] = await Promise.all([
          API.get('/brand-settings').catch(() => ({ data: {} })),
          API.get('/brand-logo').catch(() => ({ data: {} })),
        ]);

        const name = (bRes.data?.brand_name || '').trim();
        if (name) setBrandName(name);

        const hasLogo =
          !!(logoRes.data?.logo_url ||
             logoRes.data?.brand_logo_path ||
             logoRes.data?.has_logo);
        if (hasLogo) {
          setBrandLogoUrl('/api/brand-logo/image');
        } else {
          setBrandLogoUrl('/VisitOMS_logo.png');
        }
      } catch (e) {
        console.warn('Failed to load brand info for StudentProgressReport:', e);
      }
    })();
  }, []);

  // Format parent info
  useEffect(() => {
    if (report && Array.isArray(report.parents)) {
      setParentInfo(
        report.parents.map(p =>
          `${p.first_name} ${p.last_name} (${p.relation}${p.phone ? ', ' + p.phone : ''})`
        ).join(' | ')
      );
    } else {
      setParentInfo('');
    }
  }, [report]);

  const fetchReport = async () => {
    if (!filters.student_id) {
      alert('Select student!');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...filters };
      if (filters.period !== 'custom') {
        payload.date_from = '';
        payload.date_to = '';
      }
      const res = await API.post('/reports/student-progress', payload);
      setReport(res.data);
    } catch (e) {
      console.error(e);
      alert('Failed to fetch student report.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  // Excel export (usklađen sa ekranom)
  const handleExportExcel = () => {
    if (!report) return;
    const sheetData = [];
    sheetData.push([
      'Student', report.student ? report.student.name : '',
      'DOB', report.student ? formatDate(report.student.dob) : '',
      'Parents', parentInfo || '',
      'Period', `${report.date_from} - ${report.date_to}`,
      'Period Type', report.period_type || ''
    ]);
    sheetData.push([]);

    if ((report.goal_txt || '').trim()) {
      sheetData.push(['Goals']);
      sheetData.push([report.goal_txt]);
      sheetData.push([]);
    }

    const visitsByDom = report.gov_by_domain?.visits || {};

    // helper za jednu oblast
    const pushStandardDomain = (domain, skills) => {
      const prevSkills = (report.pre_baseline_skills && report.pre_baseline_skills[domain]) || [];
      const baselineIdx = skills.findIndex(s => s.is_baseline);
      const baseline = baselineIdx >= 0 ? skills[baselineIdx] : null;

      let postRows;
      if (baseline) {
        const after = skills.slice(baselineIdx + 1);
        postRows = isGov(report.raw_period)
          ? after
              .filter(s => s.status === 'achieved')
              .sort((a,b) => new Date(a.assessed_at) - new Date(b.assessed_at))
              .slice(-3)
          : after;
      } else {
        if (isGov(report.raw_period)) {
          const ach = skills
            .filter(s => s.status === 'achieved')
            .sort((a,b) => new Date(a.assessed_at) - new Date(b.assessed_at));
          postRows = ach.slice(-3);
        } else {
          postRows = skills;
        }
      }

      const hasAnySkillRow =
        prevSkills.length > 0 || !!baseline || (postRows && postRows.length > 0);

      if (!hasAnySkillRow) {
        // NEW: u GOV ako nema ničega u periodu, a ima poseta — upiši fallback 3 achieved (istorijski)
        if (isGov(report.raw_period) && (visitsByDom[domain] || []).length > 0) {
          const fb = report.gov_fallback_last3?.[domain] || [];
          if (fb.length > 0) {
            sheetData.push([domainLabels[domain] || domain]);
            sheetData.push(['Skill', 'Code', 'Status', 'Baseline', 'Assessed At', 'Comments']);
            for (const r of fb) {
              sheetData.push([
                r.skill_name, r.skill_code, 'achieved', '', formatDate(r.assessed_at), r.comments || ''
              ]);
            }
            sheetData.push([]);
          }
        }
        return;
      }

      // standardni upis
      sheetData.push([domainLabels[domain] || domain]);
      sheetData.push(['Skill', 'Code', 'Status', 'Baseline', 'Assessed At', 'Comments']);

      for (const s of prevSkills) {
        sheetData.push([s.name, s.code, 'achieved', '', '', '']);
      }

      if (baseline) {
        sheetData.push([
          baseline.skill_name,
          baseline.skill_code,
          baseline.status,
          'start',
          formatDate(baseline.assessed_at),
          baseline.comments || ''
        ]);
      }

      for (const skill of postRows) {
        sheetData.push([
          skill.skill_name,
          skill.skill_code,
          skill.status,
          '',
          formatDate(skill.assessed_at),
          skill.comments || ''
        ]);
      }

      sheetData.push([]);
    };

    if (isGov(report.raw_period)) {
      const domainsToExport = Array.from(new Set([
        ...Object.keys(report.grouped || {}),
        ...sixDomains.filter(d => (report.gov_by_domain?.visits?.[d] || []).length > 0)
      ]));

      for (const domain of domainsToExport) {
        const skills = report.grouped?.[domain] || [];
        pushStandardDomain(domain, skills);
      }

      sheetData.push([]);
      sheetData.push(['Completed visits (notes)']);
      sheetData.push(['Date', 'Time From', 'Time To', 'Teacher', 'Notes', 'Cognitive', 'Language', 'Gross motor', 'Fine motor', 'Social', 'Self help']);
      for (const v of (report.gov_monthly?.visits || [])) {
        sheetData.push([
          v.date, v.time_from || '', v.time_to || '', v.teacher_name || '',
          v.notes || '', v.notes_cognitive || '', v.notes_language || '',
          v.notes_gross_motor || '', v.notes_fine_motor || '',
          v.notes_social || '', v.notes_self_help || ''
        ]);
      }
      sheetData.push([]);
    } else {
      for (const [domain, skills] of Object.entries(report.grouped || {})) {
        pushStandardDomain(domain, skills);
      }
    }

    if ((report.achievement_text || '').trim()) {
      sheetData.push([]);
      sheetData.push(['Achievements']);
      sheetData.push([report.achievement_text]);
    }

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Student Progress');
    XLSX.writeFile(wb, 'StudentProgressReport.xlsx');
  };

  const handlePrint = () => window.print();

  // Print CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        html, body, #student-progress-report-print {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        body * { visibility: hidden !important; }
        #student-progress-report-print, #student-progress-report-print * { visibility: visible !important; }
        #student-progress-report-print {
          position: absolute; left: 0; top: 0; width: 100vw; background: white;
        }
        #student-progress-report-print thead { display: table-header-group; }
        #student-progress-report-print tfoot { display: table-footer-group; }
        #student-progress-report-print tr, #student-progress-report-print img {
          break-inside: avoid; page-break-inside: avoid;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // izračun starosti deteta u mesecima (na nivou perioda)
  const ageMonths =
    report && report.student?.dob
      ? calcAgeMonths(report.student.dob, report.date_to || new Date())
      : null;

  return (
    <div className="p-4 text-sm">
      <h2 className="text-lg font-bold mb-4 no-print">Student Progress Report</h2>

      {/* FILTERS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 mb-4 no-print">
        <select
          value={filters.student_id}
          onChange={e => setFilters(f => ({ ...f, student_id: e.target.value }))}
          className="border p-1 rounded"
        >
          <option value="">-- Select Student --</option>
          {students
            .slice()
            .sort(
              (a, b) =>
                (a.last_name || '').localeCompare(b.last_name || '') ||
                (a.first_name || '').localeCompare(b.first_name || '')
            )
            .map(s => (
              <option key={s.id} value={s.id}>
                {(s.last_name || '') + ', ' + (s.first_name || '')}
              </option>
            ))}
        </select>

        <select
          value={filters.period}
          onChange={e => setFilters(f => ({ ...f, period: e.target.value }))}
          className="border p-1 rounded"
        >
          {periodOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {filters.period === 'custom' && (
          <>
            <input
              type="date"
              value={filters.date_from}
              onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
              placeholder="Date From"
              className="border p-1 rounded"
            />
            <input
              type="date"
              value={filters.date_to}
              onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
              placeholder="Date To"
              className="border p-1 rounded"
            />
          </>
        )}
      </div>

      <div className="flex gap-2 mb-2 no-print">
        <button onClick={fetchReport} className="bg-blue-600 text-white px-4 py-2 rounded">Apply Filters</button>
        <button onClick={handleExportExcel} className="bg-green-500 text-white px-4 py-2 rounded">Export Excel</button>
        <button onClick={handlePrint} className="bg-gray-600 text-white px-4 py-2 rounded">Print</button>
      </div>

      {/* PRINT WRAPPER */}
      <div id="student-progress-report-print" ref={reportRef} className="bg-white max-w-4xl mx-auto p-2">
        {report && (
          <div className="print-header mb-4 flex flex-col gap-1 border rounded p-2 bg-gray-50">
            <div className="flex items-center gap-3">
              <img
                src={brandLogoUrl || '/VisitOMS_logo.png'}
                alt={brandName || 'Brand logo'}
                className="w-14 h-14 object-contain"
              />
              <div>
                <div className="text-base font-semibold">
                  Student Progress Report
                </div>
                {brandName && (
                  <div className="text-xs text-gray-600">
                    {brandName}
                  </div>
                )}
                <div className="text-[10px] text-gray-400">
                  Generated with VisitOMS EasyCare software
                </div>
              </div>
            </div>
            <div><strong>Student:</strong> {report.student.name}</div>
            <div><strong>Date of Birth:</strong> {formatDate(report.student.dob)}</div>
            {ageMonths !== null && (
              <div>
                <strong>Age of the child:</strong> {ageMonths} months
              </div>
            )}
            <div><strong>Parents:</strong> {parentInfo}</div>
            <div><strong>Period:</strong> {report.date_from} – {report.date_to}</div>
            <div><strong>Period Type:</strong> {report.period_type || ''}</div>
            <div><strong>Date of print:</strong> {formatDate(new Date())}</div>
          </div>
        )}

        {/* TOP PANELS: Goals + Achievements, side-by-side & equal height */}
        {report && (
          ((report.goal_txt || '').trim() || (report.achievement_text || '').trim())
        ) && (
          <div
            className={
              (report.goal_txt && report.achievement_text
                ? 'grid grid-cols-2'
                : 'grid grid-cols-1 md:grid-cols-2'
              ) + ' gap-3 items-stretch print:grid-cols-2 mb-4'
            }
          >
            {(report.goal_txt || '').trim() && (
              <div className="min-w-0 print:break-inside-avoid">
                <div className="h-full flex flex-col p-3 border border-sky-200 bg-sky-50 rounded-lg shadow-sm">
                  <div className="font-semibold text-sky-700 mb-1">Goals</div>
                  <div className="whitespace-pre-wrap break-words text-slate-700">
                    {report.goal_txt}
                  </div>
                </div>
              </div>
            )}

            {(report.achievement_text || '').trim() && (
              <div className="min-w-0 print:break-inside-avoid">
                <div className="h-full flex flex-col p-3 border border-emerald-200 bg-emerald-50 rounded-lg shadow-sm">
                  <div className="font-semibold text-emerald-700 mb-1">Achievements</div>
                  <div className="whitespace-pre-wrap break-words text-slate-700">
                    {report.achievement_text}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* BODY */}
        {loading ? (
          <div className="py-8">Loading...</div>
        ) : report ? (
          <div>
            {(() => {
              const domainsToRender = isGov(report.raw_period)
                ? Array.from(new Set([
                    ...Object.keys(report.grouped || {}),
                    ...sixDomains.filter(d => (report.gov_by_domain?.visits?.[d] || []).length > 0)
                  ]))
                : Object.keys(report.grouped || {});

              if (!domainsToRender.length) {
                return <div className="p-4">No data for selected period.</div>;
              }

              return domainsToRender.map((domain) => {
                const skills = report.grouped?.[domain] || [];
                const pre = (report.pre_baseline_skills && report.pre_baseline_skills[domain]) || [];
                const baselineIdx = skills.findIndex(s => s.is_baseline);
                const baseline = baselineIdx >= 0 ? skills[baselineIdx] : null;

                let postRows;
                if (baseline) {
                  const after = skills.slice(baselineIdx + 1);
                  postRows = isGov(report.raw_period)
                    ? after
                        .filter(s => s.status === 'achieved')
                        .sort((a,b) => new Date(a.assessed_at) - new Date(b.assessed_at))
                        .slice(-3)
                    : after;
                } else {
                  if (isGov(report.raw_period)) {
                    const ach = skills
                      .filter(s => s.status === 'achieved')
                      .sort((a,b) => new Date(a.assessed_at) - new Date(b.assessed_at));
                    postRows = ach.slice(-3);
                  } else {
                    postRows = skills;
                  }
                }

                const hasAnySkillRow =
                  pre.length > 0 || !!baseline || (postRows && postRows.length > 0);

                // NEW: fallback rows (istorijska poslednja 3 achieved) za slučaj da nema nijednog skill reda u tekućem periodu, ali ima poseta
                const vnotes = report.gov_by_domain?.visits?.[domain] || [];
                const fallbackRows = (!hasAnySkillRow && isGov(report.raw_period) && vnotes.length > 0)
                  ? (report.gov_fallback_last3?.[domain] || [])
                  : [];

                return (
                  <div key={domain} className="mb-6 print:mb-2">
                    <div className="font-semibold mb-1 text-blue-700 print:text-blue-800">{domainLabels[domain] || domain}</div>

                    {/* Skills tabela: ili normalno (ako ima sadržaja), ili fallback 3 achieved (sivo) */}
                    {hasAnySkillRow ? (
                      <table className="w-full border border-gray-200 text-xs mb-3 print:text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="border p-1">Skill</th>
                            <th className="border p-1">Code</th>
                            <th className="border p-1">Status</th>
                            <th className="border p-1">Assessed At</th>
                            <th className="border p-1">Comments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pre.map(s => (
                            <tr key={`prebase-${s.id}`} className="bg-gray-100 text-gray-400 italic">
                              <td className="border p-1">{s.name}</td>
                              <td className="border p-1">{s.code}</td>
                              <td className="border p-1">achieved</td>
                              <td className="border p-1"></td>
                              <td className="border p-1"></td>
                            </tr>
                          ))}

                          {baseline && (
                            <tr key={baseline.id} className="font-bold">
                              <td className="border p-1">{baseline.skill_name}</td>
                              <td className="border p-1">{baseline.skill_code}</td>
                              <td className="border p-1">{baseline.status}</td>
                              <td className="border p-1">{formatDate(baseline.assessed_at)}</td>
                              <td className="border p-1">{renderCommentCell(baseline.comments)}</td>
                            </tr>
                          )}

                          {postRows.map(skill => (
                            <tr key={skill.id}>
                              <td className="border p-1">{skill.skill_name}</td>
                              <td className="border p-1">{skill.skill_code}</td>
                              <td className="border p-1">{skill.status}</td>
                              <td className="border p-1">{formatDate(skill.assessed_at)}</td>
                              <td className="border p-1">{renderCommentCell(skill.comments)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : fallbackRows.length > 0 ? (
                      <table className="w-full border border-gray-200 text-xs mb-3 print:text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="border p-1">Skill</th>
                            <th className="border p-1">Code</th>
                            <th className="border p-1">Status</th>
                            <th className="border p-1">Assessed At</th>
                            <th className="border p-1">Comments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fallbackRows.map(r => (
                            <tr key={`fb-${r.id}`} className="bg-gray-100 text-gray-400">
                              <td className="border p-1">{r.skill_name}</td>
                              <td className="border p-1">{r.skill_code}</td>
                              <td className="border p-1">achieved</td>
                              <td className="border p-1">{formatDate(r.assessed_at)}</td>
                              <td className="border p-1">{renderCommentCell(r.comments)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : null}

                    {/* VISITS (samo u GOV) */}
                    {isGov(report.raw_period) && vnotes.length > 0 && (
                      <table className="w-full border border-gray-200 text-xs mb-3" aria-label="Visits">
                        <caption className="text-left text-xs font-medium text-gray-600 p-1">
                          Visits
                        </caption>
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="border p-1 w-28">Visit date</th>
                            <th className="border p-1 w-40">Teacher</th>
                            <th className="border p-1">Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vnotes.map(v => (
                            <tr key={v.id}>
                              <td className="border p-1">{v.date}</td>
                              <td className="border p-1">{v.teacher_name || ''}</td>
                              <td className="border p-1 whitespace-pre-line">{v.note}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              });
            })()}

            {/* FOOTER BRANDING */}
            <div className="mt-6 pt-2 border-t text-xs text-gray-500 text-center">
              Generated with <span className="font-semibold">VisitOMS EasyCare</span> software · https://visitoms.com
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default StudentProgressReport;







